package fr.frux.feature.favorites

import androidx.compose.ui.graphics.vector.ImageVector
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.designsystem.format.formatDistance
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.label
import fr.frux.core.maps.Location
import fr.frux.core.maps.LocationProvider
import fr.frux.feature.common.treeSeasonLabel
import fr.frux.shared.domain.favorites.FavoritesStore
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.currentHarvestMonth
import fr.frux.shared.domain.model.distanceMetersFrom
import fr.frux.shared.domain.repository.SiteRepository
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.TreeRepository
import fr.frux.shared.presentation.resolveTreeStatus
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Favoris de l'utilisateur.
 *
 * Le tri par proximité est calculé ici, côté client : la route `/api/trees/favorites` ne prend
 * pas de coordonnées, et la liste d'un utilisateur est petite. Sans position (permission
 * refusée, GPS coupé), l'ordre du serveur — du plus récemment ajouté au plus ancien — est
 * conservé tel quel.
 *
 * La liste affichée publie tout [sortedTrees] (le dernier lot chargé), chaque ligne portant son
 * propre `isFavorite` dérivé de [favoritesStore] — le store est la source unique de vérité de
 * l'appartenance, partagée avec la carte et la fiche arbre. Un retrait ne fait donc plus
 * disparaître la ligne : le cœur se vide et reste réversible en place (re-tap = re-favori),
 * protection contre une suppression par erreur ; la liste ne se compacte qu'au prochain [load]
 * (l'écran rechargeant déjà à chaque remontée dans la composition, cf. [loadJob]). Même logique
 * pour un retrait fait depuis la carte pendant que cet écran est ouvert : cœur vidé, ligne
 * conservée jusqu'au prochain chargement.
 */
class FavoritesViewModel(
    private val treeRepository: TreeRepository,
    private val locationProvider: LocationProvider,
    private val speciesRepository: SpeciesRepository,
    private val favoritesStore: FavoritesStore,
    private val siteRepository: SiteRepository,
) : ViewModel() {
    private val _state = MutableStateFlow(FavoritesState())
    val state: StateFlow<FavoritesState> = _state.asStateFlow()

    /** Mois courant, substituable en test (même pattern que `MapViewModel.timeSource`). */
    internal var currentMonth: Int = currentHarvestMonth()

    /** Horloge, substituable en test (même pattern que [currentMonth], #444). */
    @OptIn(ExperimentalTime::class)
    internal var now: Instant = Clock.System.now()

    /** Fenêtres de récolte par espèce, dérivées du référentiel une fois chargé. */
    private var speciesHarvests: Map<Long, List<HarvestWindow>> = emptyMap()

    /** Dernier lot (déjà trié par proximité) reçu, remappé si le référentiel arrive après lui. */
    private var sortedTrees: List<Tree> = emptyList()

    /** Origine utilisée pour trier/distancer [sortedTrees] ; mémorisée pour le remappage tardif du référentiel. */
    private var lastOrigin: Location? = null

    init {
        // Publie à chaque évolution du store (retrait/ajout, y compris fait depuis une autre
        // surface comme la carte) — la liste n'est jamais mise à jour "à la main" ailleurs.
        viewModelScope.launch { favoritesStore.favoriteIds.collect { publishTrees() } }
        // Tout lieu modifié dans la session (cœur basculé sur sa fiche, suppression) peut changer la liste.
        viewModelScope.launch { siteRepository.changes.collect { loadSites() } }
        loadSpecies()
        load()
    }

    /**
     * Publie la liste affichée : tout le lot chargé, sans filtrage — un retrait vide juste le
     * cœur de la ligne concernée (cf. KDoc de classe), il ne la fait plus disparaître.
     */
    private fun publishTrees() {
        val ids = favoritesStore.favoriteIds.value
        _state.update {
            it.copy(trees = sortedTrees.map { tree -> tree.toItem(lastOrigin, isFavorite = tree.id in ids) })
        }
    }

    /**
     * Un échec du référentiel espèces ne doit jamais casser l'écran : la liste s'affiche quand
     * même, simplement sans libellé de saison (statut = mapping direct du statut déclaré).
     */
    private fun loadSpecies() {
        viewModelScope.launch {
            speciesRepository.getSpecies().fold(
                onSuccess = { species ->
                    speciesHarvests = species.associate { it.id to it.harvests }
                    publishTrees()
                },
                onFailure = { Logger.d(TAG, "⚠️ Référentiel espèces indisponible : ${it.message}") },
            )
        }
    }

    /**
     * En vol, pour permettre à [load] d'être rappelé sans accumuler des requêtes concurrentes :
     * l'écran le fait à chaque remontée dans la composition (retour d'une fiche poussée par-dessus,
     * cf. [FavoritesScreen]), en plus de l'appel initial ci-dessus.
     */
    private var loadJob: Job? = null
    private var sitesJob: Job? = null

    fun load() {
        loadSites()
        loadJob?.cancel()
        loadJob =
            viewModelScope.launch {
                _state.update { it.copy(isLoading = true, error = null) }

                // Position en parallèle et jamais bloquante : la liste s'affiche dès que le serveur
                // répond, et se retrie quand la position tombe (même motif de remappage tardif que le
                // référentiel espèces). Jamais de fix frais ici — le tri par proximité ne vaut pas
                // plusieurs secondes d'écran vide ; sans dernière position connue, l'ordre serveur reste.
                // L'origine d'un chargement precedent ne vaut plus : la permission a pu etre
                // retiree depuis (#607). La position en cache revient d'ordinaire avant le serveur.
                lastOrigin = null
                launch {
                    val origin = locationProvider.lastKnownLocation() ?: return@launch
                    lastOrigin = origin
                    if (sortedTrees.isNotEmpty()) {
                        sortedTrees = sortedTrees.sortedFrom(origin)
                        publishTrees()
                    }
                }

                treeRepository.getFavoriteTrees().collect { result ->
                    result.fold(
                        onSuccess = { trees ->
                            sortedTrees = trees.sortedFrom(lastOrigin)
                            // Seed immédiat : le serveur vient de dire que ces arbres sont
                            // favoris, le store doit le savoir tout de suite — attendre son
                            // propre refresh() ferait flasher une liste vide s'il n'a pas
                            // encore abouti.
                            favoritesStore.seedAll(trees.map { it.id })
                            _state.update { it.copy(isLoading = false, error = null) }
                            publishTrees()
                        },
                        onFailure = { error ->
                            _state.update {
                                it.copy(isLoading = false, error = error.asAppError())
                            }
                        },
                    )
                }
            }
    }

    /** Section secondaire : un échec garde la dernière liste connue et ne bloque jamais les arbres. */
    private fun loadSites() {
        sitesJob?.cancel()
        sitesJob =
            viewModelScope.launch {
                siteRepository
                    .favoriteSites()
                    .onSuccess { sites -> _state.update { it.copy(favoriteSites = sites) } }
                    .onFailure { Logger.d(TAG, "Lieux suivis indisponibles : ${it.message}") }
            }
    }

    /**
     * Bascule via le [FavoritesStore] : le cœur se vide/remplit immédiatement (optimisme) et
     * revient en arrière sur refus (rollback central) — la ligne, elle, ne bouge jamais d'ici,
     * elle n'est qu'un item de `sortedTrees` avec un `isFavorite` recalculé.
     */
    fun onToggleFavorite(treeId: Long) {
        viewModelScope.launch {
            favoritesStore.toggle(treeId).onFailure { error ->
                _state.update { it.copy(transientError = error.asAppError()) }
            }
        }
    }

    /** Consomme le message ponctuel une fois affiché (le snackbar ne doit pas se rejouer). */
    fun onErrorShown() {
        _state.update { it.copy(transientError = null) }
    }

    private fun List<Tree>.sortedFrom(origin: Location?): List<Tree> =
        if (origin == null) {
            this
        } else {
            sortedBy { Location(it.latitude, it.longitude).distanceMetersTo(origin) }
        }

    @OptIn(ExperimentalTime::class)
    private fun Tree.toItem(
        origin: Location?,
        isFavorite: Boolean,
    ): FavoriteTree {
        val meters = distanceMetersFrom(origin)
        val distanceText = meters?.let { "à ${formatDistance(it)}" } ?: "Distance inconnue"
        val harvests = speciesHarvests[species.id].orEmpty()
        val resolvedStatus = resolveTreeStatus(status, harvests, currentMonth, lastObservedAt, now)
        val seasonLabel = treeSeasonLabel(harvests, currentMonth)
        return FavoriteTree(
            id = id,
            name = species.commonNameFr,
            meta = listOfNotNull(distanceText, resolvedStatus.label, seasonLabel).joinToString(" · "),
            status = resolvedStatus,
            glyph = glyphFor(species.scientificName, harvestPart?.name),
            scientificName = species.scientificName,
            isFavorite = isFavorite,
            photoUrl = photoUrl,
        )
    }

    companion object {
        private const val TAG = "FavoritesViewModel"
    }
}

data class FavoritesState(
    val trees: List<FavoriteTree> = emptyList(),
    /** Lieux suivis (ADR 0035), affichés au-dessus des arbres. */
    val favoriteSites: List<Site> = emptyList(),
    val isLoading: Boolean = false,
    /** Erreur bloquante : la liste n'a pas pu être chargée. Libellé résolu par l'écran (#176). */
    val error: AppError? = null,
    /** Erreur ponctuelle (retrait refusé) : affichée en snackbar, la liste reste utilisable. */
    val transientError: AppError? = null,
)

data class FavoriteTree(
    val id: Long,
    val name: String,
    val meta: String,
    val status: FruxStatus,
    val isFavorite: Boolean,
    val glyph: ImageVector? = null,
    /** Pour teinter la pastille comme le marqueur (`treeMarkerTint`). */
    val scientificName: String = "",
    val photoUrl: String? = null,
)
